import React from "react";
import {
  Box,
  Typography,
  Slider,
  Button,
  TextField,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useSelector } from "react-redux";

const FilterProperty = ({ filters, setFilters }) => {
  const theme = useSelector((state) => state.themeValue.colors);

  // --- Handle input field changes ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // --- Handle slider change for property ---
  const handleSliderChange = (key) => (event, newValue) => {
    setFilters((prev) => ({
      ...prev,
      [key]: newValue,
    }));
  };

  // --- Handle slider change for villa/apartment ---
  const villahandleSliderChange = (key) => (event, newValue) => {
    setFilters((prev) => ({
      ...prev,
      [key]: newValue,
    }));
  };

  // --- Format numbers into ₹ notation ---
  const formatPrice = (value) => {
    if (value >= 10000000) return `₹${(value / 10000000).toFixed(1)} Cr`;
    if (value >= 100000) return `₹${(value / 100000).toFixed(1)} L`;
    return `₹${value.toLocaleString()}`;
  };

  const villaformatPrice = formatPrice;

  // --- Reset all filters based on type ---
const handleReset = () => {
  setFilters({
    type: "",
    priceRange: [0, 100000000],
    minSize: 0,
    maxSize: 10000,
    marketPriceRange: [0, 100000000],
    sizeRange: [0, 10000],
    villaPrice: [50000, 100000000],
    villaMinSize: 100,
    villaMaxSize: 100000,
  });
};


  // --- Type Labels ---
  const propertyTypes = [

    { label: "Plots / Land", value: "property" },
    { label: "Project Plots", value: "project" },
    { label: "Independent House / Villa", value: "Villa" },
    { label: "Flat / Apartment", value: "apartment" },
  ];

  
  const getHeading = (type) => {
    if (!type) return "Filter Properties";

    const selected = propertyTypes.find((t) => t.value === type);
    return `Filter ${selected?.label || type}`;
  };


  return (
    <Box
      sx={{
        ...styles.container,
        backgroundColor: theme.background,
        boxShadow: `0 0px 1px ${theme.text}`,
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 5,
        }}
      >
        <Typography
          variant="h6"
          sx={{ ...styles.heading, color: theme.text }}
        >
          {getHeading(filters.type)}
        </Typography>

        <Button
          variant="outlined"
          size="small"
          startIcon={<RestartAltIcon />}
          onClick={handleReset}
          sx={{
            textTransform: "none",
            borderColor: "#ccc",
            color: theme.text,
          }}
        >
          Reset
        </Button>
      </Box>

      {/* Property Type Buttons */}
      <Box sx={styles.formGroup}>
        <Typography sx={{ ...styles.label, color: theme.text }}>
          Property Type
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {propertyTypes.map(({ label, value }) => (
            <Button
              key={value}
              variant={filters.type === value ? "contained" : "outlined"}
              onClick={() => setFilters((prev) => ({ ...prev, type: value }))}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                px: 2.5,
                py: 1,
                fontWeight: 500,
                color: filters.type === value ? "#fff" : theme.text,
                backgroundColor:
                  filters.type === value ? "#007bff" : "transparent",
                borderColor: "#ccc",
                "&:hover": {
                  backgroundColor:
                    filters.type === value ? "#0066cc" : "#f0f0f0",
                },
              }}
            >
              {label}
            </Button>
          ))}

        </Box>
      </Box>

      {/* No type selected */}
      {filters.type === "" && (
        <Typography sx={{ color: theme.text, mt: 2 }}>
          Please select a property type to apply filters.
        </Typography>
      )}

      {/* Property Filter */}
      {filters.type === "property" && (
        <>
          <Box sx={styles.formGroup}>
            <Typography sx={{ ...styles.label, color: theme.text }}>
              SqFt Price Range
            </Typography>
            <Slider
              value={filters.priceRange || [0, 100000]}
              onChange={handleSliderChange("priceRange")}
              valueLabelDisplay="auto"
              valueLabelFormat={formatPrice}
              min={0}
              max={100000}
              step={1000}
              sx={{ color: "#007bff", mt: 1 }}
            />
            <Typography variant="body2" sx={{ color: theme.text }}>
              {formatPrice(filters.priceRange?.[0] || 0)} -{" "}
              {formatPrice(filters.priceRange?.[1] || 100000)}
            </Typography>
          </Box>

          <Box sx={styles.formGroup}>
            <Typography sx={{ ...styles.label, color: theme.text }}>
              Size Range (In SqFt)
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Min Sqft"
                name="minSize"
                type="number"
                fullWidth
                value={filters.minSize}
                onChange={handleChange}
                size="small"
                sx={getTextFieldStyles(theme)}
              />
              <TextField
                label="Max Sqft"
                name="maxSize"
                type="number"
                fullWidth
                value={filters.maxSize}
                onChange={handleChange}
                size="small"
                sx={getTextFieldStyles(theme)}
              />
            </Box>
          </Box>
        </>
      )}

      {/* Project Filter */}
      {filters.type === "project" && (
        <>
          <Box sx={styles.formGroup}>
            <Typography sx={{ ...styles.label, color: theme.text }}>
              SqFt Price Range (₹)
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Min Price"
                name="marketMinPrice"
                type="number"
                fullWidth
                value={filters.marketPriceRange?.[0] || ""}
                onChange={(e) => {
                  const newMin = parseInt(e.target.value) || 0;
                  setFilters((prev) => ({
                    ...prev,
                    marketPriceRange: [
                      newMin,
                      prev.marketPriceRange?.[1] || 0,
                    ],
                  }));
                }}
                size="small"
                sx={getTextFieldStyles(theme)}
              />
              <TextField
                label="Max Price"
                name="marketMaxPrice"
                type="number"
                fullWidth
                value={filters.marketPriceRange?.[1] || ""}
                onChange={(e) => {
                  const newMax = parseInt(e.target.value) || 0;
                  setFilters((prev) => ({
                    ...prev,
                    marketPriceRange: [
                      prev.marketPriceRange?.[0] || 0,
                      newMax,
                    ],
                  }));
                }}
                size="small"
                sx={getTextFieldStyles(theme)}
              />
            </Box>
          </Box>

          <Box sx={styles.formGroup}>
            <Typography sx={{ ...styles.label, color: theme.text }}>
              Size Range (sqft)
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Min Size"
                name="sizeMin"
                type="number"
                fullWidth
                value={filters.sizeRange?.[0] || ""}
                onChange={(e) => {
                  const newMin = parseInt(e.target.value) || 0;
                  setFilters((prev) => ({
                    ...prev,
                    sizeRange: [newMin, prev.sizeRange?.[1] || 0],
                  }));
                }}
                size="small"
                sx={getTextFieldStyles(theme)}
              />
              <TextField
                label="Max Size"
                name="sizeMax"
                type="number"
                fullWidth
                value={filters.sizeRange?.[1] || ""}
                onChange={(e) => {
                  const newMax = parseInt(e.target.value) || 0;
                  setFilters((prev) => ({
                    ...prev,
                    sizeRange: [prev.sizeRange?.[0] || 0, newMax],
                  }));
                }}
                size="small"
                sx={getTextFieldStyles(theme)}
              />
            </Box>
          </Box>
        </>
      )}

      {/* Villa / Apartment Filter */}
      {(filters.type === "Villa" || filters.type === "apartment") && (
        <>
          <Box sx={styles.formGroup}>
            <Typography sx={{ ...styles.label, color: theme.text }}>
              Total Price Range
            </Typography>
            <Slider
              value={filters.villaPrice || [50000, 100000000]}
              onChange={villahandleSliderChange("villaPrice")}
              valueLabelDisplay="auto"
              valueLabelFormat={villaformatPrice}
              min={50000}
              max={100000000}
              step={1000}
              sx={{ color: "#007bff", mt: 1 }}
            />
            <Typography variant="body2" sx={{ color: theme.text }}>
              {villaformatPrice(filters.villaPrice?.[0] || 0)} -{" "}
              {villaformatPrice(filters.villaPrice?.[1] || 100000)}
            </Typography>
          </Box>

          <Box sx={styles.formGroup}>
            <Typography sx={{ ...styles.label, color: theme.text }}>
              Square Footage
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                label="Min Sqft"
                name="minSize"
                type="number"
                fullWidth
                value={filters.minSize}
                onChange={handleChange}
                size="small"
                sx={getTextFieldStyles(theme)}
              />
              <TextField
                label="Max Sqft"
                name="maxSize"
                type="number"
                fullWidth
                value={filters.maxSize}
                onChange={handleChange}
                size="small"
                sx={getTextFieldStyles(theme)}
              />
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

const getTextFieldStyles = (theme) => ({
  "& .MuiOutlinedInput-root": {
    color: theme.text,
    "& fieldset": {
      borderColor: theme.text,
    },
    "&:hover fieldset": {
      borderColor: theme.text,
    },
    "&.Mui-focused fieldset": {
      borderColor: theme.text,
    },
  },
  "& input": {
    color: theme.text,
  },
});

const styles = {
  container: {
    maxWidth: "100%",
    padding: "2rem",
    borderRadius: "10px",
    position: "sticky",
    top: "100px",
    alignSelf: "flex-start",
    height: "fit-content",
    marginBottom: "20px",
    minHeight: "65vh",
  },
  heading: {
    textAlign: "center",
    fontWeight: 600,
  },
  formGroup: {
    marginBottom: "1.5rem",
  },
  label: {
    fontWeight: 500,
    marginBottom: "0.5rem",
    display: "block",
  },
};

export default FilterProperty;
